import React, { FC, useCallback } from 'react';
import { Grid, IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import SelectField from 'components/select-field';
import ContainerModal from './modal-container';
import { BaseButton } from 'components/BaseButton';
import { useMutation } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateUserPermissions } from 'graphql/users/mutations';
import { roleType } from 'v2-types/user';
import { ListingEntry } from 'graphql/listings/queries';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: any) => ({
  listHeader: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      gap: theme.spacing(1),
      padding: theme.spacing(0.5, 1),
      backgroundColor: theme.palette.button.default,
      color: theme.palette.common.black,
      borderRadius: theme.spacing(0.5),
    },
  },
  headerElement: { flex: 1, fontWeight: 500 },
  headerKind: { width: '30%', fontWeight: 500 },
  headerAction: { width: theme.spacing(5.5) },
  // The dialog body owns scrolling on phones; a bounded list region only from sm.
  list: {
    minHeight: theme.spacing(25),
    [theme.breakpoints.up('sm')]: {
      maxHeight: theme.spacing(37.5),
      overflowY: 'auto',
    },
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0, 1),
    padding: theme.spacing(0.5, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  element: {
    flex: 1,
    minWidth: 0,
    fontWeight: 500,
    overflowWrap: 'anywhere',
  },
  kind: {
    order: 3,
    width: '100%',
    color: theme.palette.text.shadow,
    [theme.breakpoints.up('sm')]: {
      order: 0,
      width: '30%',
      color: 'inherit',
    },
  },
  deleteButton: {
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    backgroundColor: theme.palette.button.default,
    '@media (hover: none) and (pointer: coarse)': {
      minWidth: theme.spacing(5.5),
      minHeight: theme.spacing(5.5),
    },
  },
  deleteIcon: {
    fontSize: 14,
    color: theme.palette.common.black,
  },
}));

type permissionsType = {
  element?: string;
  kind?: string;
};

type formCreatePermission = {
  permissionsSelected: permissionsType[];
  type: 'screen' | 'query' | 'mutation' | 'role';
  selectedPermission: string;
};

type PermissionFormProps = {
  permissions: permissionsType[];
  open: boolean;
  onClose: () => void;
  partyId: string;
  onSuccess: (permissions: any) => void;
  existingMutations: ListingEntry[];
  existingQuerys: ListingEntry[];
  existingRoles: roleType[];
};

const validTypes = [{ type: 'role' }, { type: 'query' }, { type: 'mutation' }];

const PermissionsForm: FC<PermissionFormProps> = ({
  permissions,
  open,
  onClose,
  partyId,
  onSuccess,
  existingMutations,
  existingQuerys,
  existingRoles,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const getKindnames = {
    query: t('contact:accessInformationLabel'),
    mutation: t('contact:updateInformationLabel'),
    role: t('contact:roleLabel'),
  };

  const methods = useForm<formCreatePermission>({
    defaultValues: {
      permissionsSelected: permissions.map((permissionLocal) => {
        if (permissionLocal.kind !== 'role') {
          return permissionLocal;
        }
        return {
          kind: permissionLocal.kind,
          element: existingRoles.find((f) => f.description === permissionLocal.element)?._id,
        };
      }),
      type: 'query',
      selectedPermission: '',
    },
  });
  const { control } = methods;

  const watchedType = useWatch({ control, name: 'type' });
  const { mutate: updatepermission, ...updatePermissionResponse } = useMutation(
    updateUserPermissions,
    {
      onSuccess: (data) => {
        updatePermissionResponse.reset();
        onSuccess(data.updatePartyPermissions?.permissions || []);
        onClose();
      },
    }
  );
  const getOptions = useCallback(
    (type) => {
      switch (type) {
        case 'role':
          return (
            existingRoles.sort((entryA, entryB) =>
              entryA.description.localeCompare(entryB.description)
            ) || []
          );
        case 'mutation':
          return (
            existingMutations.sort((entryA, entryB) =>
              entryA.description.localeCompare(entryB.description)
            ) || []
          );
        case 'query':
          return (
            existingQuerys.sort((entryA, entryB) =>
              entryA.description.localeCompare(entryB.description)
            ) || []
          );
        default:
          return [];
      }
    },
    [existingMutations, existingQuerys, existingRoles]
  );

  const PermissionsArray = useFieldArray({ control, name: 'permissionsSelected' });
  const isSaving = updatePermissionResponse.isLoading;

  return (
    <ContainerModal
      open={open}
      onClose={onClose}
      onCancel={onClose}
      onSave={() => {
        methods.handleSubmit((data) =>
          updatepermission({
            id: partyId,
            data: data.permissionsSelected
              .filter((f) => f.element && f.kind)
              .map((f) => ({ element: f.element!, kind: f.kind! })),
          })
        )();
      }}
      loadingSaveButton={isSaving}
      modalTitle={t('contact:managePermissionsModalTitle')}
      maxWidth="md"
    >
      <Grid container direction="column" spacing={3}>
        {/* Picker row: stacked on phones, kind / element / add from sm. */}
        <Grid item>
          <Grid container direction="row" spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={3}>
              <Controller
                control={control}
                name="type"
                render={({ field: { ref, ...field } }) => (
                  <SelectField
                    {...field}
                    inputRef={ref}
                    disabled={isSaving}
                    value={field.value}
                    data={validTypes}
                    dataKey="type"
                    dataValue="type"
                    dataText="type"
                    label={t('contact:kindLabel')}
                    handleChange={(e) => {
                      field.onChange(e);
                      methods.setValue('selectedPermission', '');
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <Controller
                control={control}
                name="selectedPermission"
                render={({ field: { ref, ...field } }) => (
                  <SelectField
                    {...field}
                    inputRef={ref}
                    disabled={isSaving}
                    value={field.value}
                    data={getOptions(watchedType)}
                    dataKey={watchedType !== 'role' ? 'code' : '_id'}
                    emptyValue
                    dataText="description"
                    dataValue={watchedType !== 'role' ? 'code' : '_id'}
                    label={t('contact:permissionElementLabel')}
                    handleChange={field.onChange}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <BaseButton
                width="100%"
                icon="plus"
                text={t('contact:addButton')}
                disabled={isSaving}
                onClick={() => {
                  const { append } = PermissionsArray;
                  const { type, selectedPermission } = methods.getValues();
                  append({
                    element: selectedPermission,
                    kind: type,
                  });
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <div className={classes.listHeader}>
            <Typography variant="body2" className={classes.headerElement}>
              {t('contact:permissionElementLabel')}
            </Typography>
            <Typography variant="body2" className={classes.headerKind}>
              {t('contact:kindLabel')}
            </Typography>
            <span className={classes.headerAction} />
          </div>
          <div className={classes.list}>
            {PermissionsArray.fields.map((permission, i) => {
              const currentOptions = permission.kind ? getOptions(permission.kind) : [];
              const description =
                permission.kind !== 'role'
                  ? (currentOptions as any[]).find((f) => f.code === permission.element)
                    ?.description
                  : (currentOptions as any[]).find((f) => f._id === permission.element)
                    ?.description;
              return (
                <div key={permission.id} className={classes.row}>
                  <Typography variant="body2" className={classes.element}>
                    {description || ''}
                  </Typography>
                  <Typography variant="body2" className={classes.kind}>
                    {permission.kind ? getKindnames[permission.kind] : ''}
                  </Typography>
                  <IconButton
                    aria-label={`${t('common:delete')} ${description || ''}`.trim()}
                    disabled={isSaving}
                    className={classes.deleteButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      PermissionsArray.remove(i);
                    }}
                    size="large"
                  >
                    <FontAwesomeIcon icon="trash" className={classes.deleteIcon} />
                  </IconButton>
                </div>
              );
            })}
          </div>
        </Grid>
      </Grid>
    </ContainerModal>
  );
};

export default PermissionsForm;
